import React, { FC, useState } from 'react'
import { Switch } from '@headlessui/react'
import {
  BaseModal,
  CommonExamBoardSelectModalContent,
} from '@/components/ui/common/modal'
import { ExamBoards, ICourse } from '@/types/firebase'

interface Props {
  handleSwitcherChange: (value: boolean) => Promise<void>
  isLoading?: boolean
  isChecked?: boolean
  selectExamBoardModalProps?: {
    id: string
    isSelectExamBoardModalOpened: boolean
    onClose: () => void
    course: ICourse
    handleConfirmClick: (examBoard: ExamBoards) => Promise<void>
    customZIndexClass?: string | undefined
    isLoading?: boolean
  }
  popover?: React.ReactNode
  wrapperClassName?: string
}

export const SelectCourseSwitcher: FC<Props> = ({
  handleSwitcherChange,
  isLoading,
  isChecked,
  selectExamBoardModalProps,
  popover = (
    <div
      className={`absolute z-[5] bottom-[-28px] left-[60px] px-4 py-2 bg-white rounded-tr-md rounded-b-md  lg:block`}
    >
      <div
        className={`before:absolute before:content-[''] before:w-0 before:h-0 before:border-t-[8px] before:border-b-[8px] before:border-l-[6px] before:border-t-transparent before:border-b-transparent before:border-l-white before:left-[0] before:top-[-8px]`}
      >
        <p className={`text-xs text-black text-nowrap font-semibold`}>
          {isChecked ? `Deselect Course` : `Select Course`}
        </p>
      </div>
    </div>
  ),
  wrapperClassName = `relative px-2 py-2 flex items-center justify-center rounded-full bg-white/50 backdrop-blur-sm`,
}) => {
  const [isSwitcherHovered, setIsSwitcherHovered] = useState(false)

  return (
    <>
      <div
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        className={wrapperClassName}
        style={{ cursor: 'default' }}
      >
        <Switch
          disabled={isLoading}
          onChange={handleSwitcherChange}
          checked={isChecked}
          onMouseEnter={() => setIsSwitcherHovered(true)}
          onMouseLeave={() => setIsSwitcherHovered(false)}
          className={`inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 ease-in-out focus:ring-0 focus:outline-none focus:shadow-none focus:ring-offset-0 ${
            isChecked ? 'bg-v8-blue' : 'bg-v8-line'
          }`}
        >
          <span className="sr-only">Select/Deselect course</span>
          <span
            aria-hidden="true"
            className={` inline-block h-4 w-4 rounded-full shadow ring-0 transition-transform duration-600 ease-in-out transform ${
              isChecked ? 'translate-x-6 bg-white' : 'translate-x-1 bg-white'
            }`}
          />
        </Switch>

        {/* Select/Deselect Course Popover */}
        {popover && isSwitcherHovered && <>{popover}</>}
      </div>

      {/* Select ExamBoard Modal */}
      {selectExamBoardModalProps && (
        <BaseModal
          id={selectExamBoardModalProps.id}
          key={selectExamBoardModalProps.id}
          isOpen={selectExamBoardModalProps.isSelectExamBoardModalOpened}
          onClose={selectExamBoardModalProps.onClose}
          content={
            <CommonExamBoardSelectModalContent
              course={selectExamBoardModalProps.course}
              handleConfirmSelection={
                selectExamBoardModalProps.handleConfirmClick
              }
              isLoading={selectExamBoardModalProps.isLoading}
            />
          }
          bodyAdditionalClassName="max-sm:w-full sm:min-w-[400px]"
          customZIndexClass={selectExamBoardModalProps.customZIndexClass}
          bodyBasicClassName="relative transition-all transform bg-white rounded-v8 border border-v8-line shadow-v8-card p-6"
        />
      )}
    </>
  )
}
